"use client";

import React, { useState, useEffect } from "react";
import { Skeleton, Modal, Form, Input, Select, message } from "antd";
import usePublicPlacesStore, { Place } from "./PublicPlacesStore.ts";
import usePlacesStore from "../Places/PlacesStore.ts";
import useDomainStore from "../../stores/domainStore.ts";
import StatusBadge from "./components/StatusBadge.tsx";
import PlaceDetailModal from "./components/PlaceDetailModal.tsx";

const { TextArea } = Input;
const { Option } = Select;



const PlaceCard = ({ place, onOpenDetail }: { place: Place; onOpenDetail: (place: Place) => void }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="group cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onOpenDetail(place)}
    >
      <div
        className={`relative bg-surface rounded-[32px] p-6 h-full transition-all duration-300 ease-out ${isHovered
          ? "shadow-[0_20px_40px_-12px_rgba(0,0,0,0.12)] scale-[1.02] -translate-y-1"
          : "shadow-soft"
          }`}
        style={{
          border: "1px solid rgba(0, 0, 0, 0.04)",
        }}
      >
        {/* Image */}
        <div
          className={`relative w-full h-48 rounded-[24px] mb-4 overflow-hidden transition-all duration-300 ${isHovered ? "scale-105" : "scale-100"
            }`}
        >
          {place.image ? (
            <img
              src={place.image}
              alt={place.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div
              className="w-full h-full"
              style={{
                background: `linear-gradient(135deg, ${place.status === "open" ? "#FFCB37" : "#E6B000"} 0%, ${place.status === "open" ? "#E6B000" : "#B38900"} 100%)`,
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <i className="fa-solid fa-location-dot text-white text-5xl opacity-30" />
              </div>
            </div>
          )}
          {place.rating && place.rating > 0 && (
            <div className="absolute top-3 right-3 bg-surface/95 backdrop-blur-sm rounded-full px-3 py-1.5 flex items-center gap-1.5 shadow-sm">
              <i className="fa-solid fa-star text-primary-400 text-xs" />
              <span className="text-xs font-semibold text-secondary-900">
                {place.rating.toFixed(1)}
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-xl font-semibold text-secondary-900 leading-tight flex-1 group-hover:text-primary-500 transition-colors duration-200">
              {place.name}
            </h3>
            <StatusBadge status={place.status} />
          </div>

          <p className="text-sm text-secondary-600 leading-relaxed line-clamp-2">
            {place.description}
          </p>

          <div className="flex items-center gap-2 text-sm text-secondary-500">
            <i className="fa-solid fa-location-dot text-primary-400" />
            <span className="truncate">{place.address}</span>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-secondary-100">
            <span
              className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-50 text-secondary-700 transition-all duration-200 group-hover:bg-primary-50 group-hover:text-primary-700"
            >
              {place.category}
            </span>
            <div
              className={`flex items-center gap-1.5 text-primary-400 transition-all duration-200 ${isHovered ? "translate-x-1" : "translate-x-0"
                }`}
            >
              <span className="text-xs font-semibold">Xem thêm</span>
              <i className="fa-solid fa-arrow-right text-xs" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function PublicPlaces() {
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isLoadMoreHovered, setIsLoadMoreHovered] = useState(false);
  const [createTripForm] = Form.useForm();
  const { isSaving } = usePlacesStore();

  // Get current domain from store
  const { currentDomain, initializeDomain } = useDomainStore();

  // Get all state and actions from store
  const {
    places,
    isLoading,
    isLoadingMore,
    error,
    currentPage,
    hasMore,
    total,
    activityGroups,
    isLoadingGroups,
    isCreateTripModalOpen,
    isPlaceModalOpen,
    selectedPlace,
    placeDetail,
    isLoadingPlaceDetail,
    isHeaderVisible,
    lastScrollY,
    domainClickCount,
    fetchPlaces,
    fetchActivityGroups,
    handleLoadMore,
    handleCreateTrip,
    openPlaceDetail,
    closePlaceDetail,
    setCreateTripModalOpen,
    setHeaderVisible,
    setLastScrollY,
    handleVersionClick,
  } = usePublicPlacesStore();

  useEffect(() => {
    initializeDomain();
    fetchPlaces(1, false);
  }, [initializeDomain, fetchPlaces]);

  // Load Activity Groups for header
  useEffect(() => {
    fetchActivityGroups();
  }, [fetchActivityGroups]);

  // Handle scroll to hide/show header
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show header when at top or scrolling up
      if (currentScrollY < 100) {
        setHeaderVisible(true);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show header
        setHeaderVisible(true);
      } else if (currentScrollY > lastScrollY) {
        // Scrolling down - hide header
        setHeaderVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY, setHeaderVisible, setLastScrollY]);

  // Infinite scroll - Load more when scrolled 50%
  useEffect(() => {
    const handleInfiniteScroll = () => {
      // Don't trigger if already loading or no more data
      if (isLoadingMore || !hasMore || isLoading) return;

      // Calculate scroll percentage
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const clientHeight = window.innerHeight;

      const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;

      // Trigger load more at 50%
      if (scrollPercentage >= 50) {
        console.log('📜 Infinite scroll triggered at 50%');
        handleLoadMore();
      }
    };

    window.addEventListener("scroll", handleInfiniteScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleInfiniteScroll);
  }, [isLoadingMore, hasMore, isLoading, handleLoadMore]);

  const handleOpenCreateTrip = () => {
    setCreateTripModalOpen(true);
  };

  const handleCloseCreateTrip = () => {
    setCreateTripModalOpen(false);
    createTripForm.resetFields();
  };

  const onFinishCreateTrip = async () => {
    try {
      const values = await createTripForm.validateFields();
      await handleCreateTrip(values);
      createTripForm.resetFields();
    } catch (err) {
      console.error("Error validating form:", err);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header Section */}
      <div
        className={`fixed top-0 left-0 right-0 bg-white border-b  z-40 transition-transform duration-300 ease-in-out ${isHeaderVisible ? "translate-y-0" : "-translate-y-full"
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {isLoadingGroups ? (
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              {[...Array(6)].map((_, index) => (
                <Skeleton.Button
                  key={index}
                  active
                  size="default"
                  shape="round"
                  style={{ width: 120, height: 40 }}
                />
              ))}
            </div>
          ) : activityGroups.length > 0 ? (
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              {activityGroups.map((group) => (
                <button
                  key={group.id}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-400 hover:bg-primary-500 shadow-sm hover:shadow-md transition-all duration-200 group transform hover:scale-105 active:scale-95"
                  onClick={() => {
                    // Handle filter by activity group
                    console.log("Filter by:", group.key);
                  }}
                >
                  <span className="text-black font-semibold text-sm sm:text-base group-hover:text-black">
                    {group.name}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-secondary-500 text-sm">Không có nhóm hoạt động nào</p>
            </div>
          )}
        </div>
      </div>

      {/* Places Grid */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-12 sm:py-16 pt-32 sm:pt-40 flex-1">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 lg:gap-8 w-full">
            {[...Array(10)].map((_, index) => (
              <div
                key={index}
                className="bg-surface rounded-[32px] p-6 shadow-soft"
                style={{ border: "1px solid rgba(0, 0, 0, 0.04)" }}
              >
                <div className="w-full h-48 rounded-[24px] mb-4 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer bg-[length:200%_100%]" />
                <Skeleton active paragraph={{ rows: 3 }} title={{ width: "80%" }} />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-red-50 border border-red-200">
              <i className="fa-solid fa-circle-exclamation text-red-600" />
              <span className="text-red-700 font-medium">{error}</span>
            </div>
          </div>
        ) : places.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-secondary-50 border border-secondary-200">
              <i className="fa-solid fa-inbox text-secondary-600" />
              <span className="text-secondary-700 font-medium">
                Chưa có địa điểm nào
              </span>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 lg:gap-8 w-full">
              {places.map((place) => (
                <PlaceCard key={place.id} place={place} onOpenDetail={openPlaceDetail} />
              ))}
            </div>

            {/* Load More Section */}
            {hasMore && (
              <div className="flex justify-center items-center mt-12">
                {isLoadingMore ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 lg:gap-8 w-full">
                    {[...Array(5)].map((_, index) => (
                      <div
                        key={index}
                        className="bg-surface rounded-[32px] p-6 shadow-soft"
                        style={{ border: "1px solid rgba(0, 0, 0, 0.04)" }}
                      >
                        <div className="w-full h-48 rounded-[24px] mb-4 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer bg-[length:200%_100%]" />
                        <Skeleton active paragraph={{ rows: 3 }} title={{ width: "80%" }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <button
                    onClick={handleLoadMore}
                    onMouseEnter={() => setIsLoadMoreHovered(true)}
                    onMouseLeave={() => setIsLoadMoreHovered(false)}
                    className={`inline-flex items-center gap-3 px-8 py-4 rounded-[32px] font-semibold text-base transition-all duration-300 ease-out ${isLoadMoreHovered
                      ? "bg-primary-500 shadow-[0_12px_24px_-8px_rgba(255,203,55,0.4)] scale-105 -translate-y-0.5"
                      : "bg-primary-400 shadow-[0_4px_16px_-4px_rgba(255,203,55,0.3)] scale-100 translate-y-0"
                      }`}
                    style={{
                      color: "#000000",
                    }}
                  >
                    <span>Tải thêm địa điểm</span>
                    <i
                      className={`fa-solid fa-arrow-down transition-transform duration-300 ${isLoadMoreHovered ? "translate-y-1" : "translate-y-0"
                        }`}
                    />
                  </button>
                )}
              </div>
            )}

            {/* Show total count if available */}
            {total > 0 && (
              <div className="text-center mt-8">
                <p className="text-sm text-secondary-500">
                  Hiển thị {places.length} / {total} địa điểm
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-surface border-t border-secondary-100 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            {/* Brand & Copyright */}
            <div className="flex flex-col items-center sm:items-start gap-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200">
                <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">
                  Trove
                </span>
              </div>
              <p className="text-xs text-secondary-500 text-center sm:text-left">
                © {new Date().getFullYear()} Trove. All rights reserved.
              </p>
              <p
                className={`text-[10px] text-center sm:text-left cursor-pointer transition-colors ${currentDomain === "dev" || currentDomain === "local"
                  ? "text-purple-500 hover:text-purple-600"
                  : "text-secondary-400 hover:text-secondary-600"
                  }`}
                onClick={handleVersionClick}
                title={`Click ${10 - (domainClickCount || 0)} more times to switch API domain`}
              >
                {currentDomain === "dev" || currentDomain === "local" ? "Dev" : "v1.0.0"}
              </p>
            </div>

            {/* Download Links */}
            <div className="flex items-center gap-4">
              <a
                href="https://apps.apple.com/us/app/trove-d%E1%BA%ABn-%C4%91%C6%B0%E1%BB%9Dng-m%E1%BB%8Di-cu%E1%BB%99c-h%E1%BA%B9n/id6757320991"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-black hover:bg-secondary-900 text-white transition-all duration-200 hover:scale-105 active:scale-95 shadow-sm hover:shadow-md"
              >
                <i className="fa-brands fa-apple text-lg" />
                <div className="flex flex-col items-start">
                  <span className="text-[10px] leading-tight">Download on the</span>
                  <span className="text-sm font-semibold leading-tight">App Store</span>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=trove.explore.place.tev"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-black hover:bg-secondary-900 text-white transition-all duration-200 hover:scale-105 active:scale-95 shadow-sm hover:shadow-md"
              >
                <i className="fa-brands fa-google-play text-lg" />
                <div className="flex flex-col items-start">
                  <span className="text-[10px] leading-tight">Download on the</span>
                  <span className="text-sm font-semibold leading-tight">Google Play</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Action Button */}
      <button
        onClick={handleOpenCreateTrip}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary-400 hover:bg-primary-500 shadow-[0_8px_24px_-4px_rgba(255,203,55,0.4)] hover:shadow-[0_12px_32px_-4px_rgba(255,203,55,0.5)] transition-all duration-300 ease-out flex items-center justify-center group hover:scale-110 active:scale-95"
        style={{
          color: "#000000",
        }}
        aria-label="Tạo chuyến mới"
      >
        <i className="fa-solid fa-plus text-2xl group-hover:rotate-90 transition-transform duration-300" />
      </button>

      {/* Place Detail Modal */}
      <PlaceDetailModal
        open={isPlaceModalOpen}
        onCancel={closePlaceDetail}
        isLoading={isLoadingPlaceDetail}
        selectedPlace={selectedPlace}
        placeDetail={placeDetail}
      />

      {/* Create Trip Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-plus-circle text-primary-400" />
            <span className="text-lg font-semibold">Tạo chuyến mới</span>
          </div>
        }
        open={isCreateTripModalOpen}
        onCancel={handleCloseCreateTrip}
        onOk={onFinishCreateTrip}
        confirmLoading={isSaving}
        okText="Tạo chuyến"
        cancelText="Hủy"
        width={600}
        okButtonProps={{
          className: "bg-primary-400 hover:bg-primary-500 border-none text-black font-semibold",
        }}
      >
        <Form
          form={createTripForm}
          layout="vertical"
          className="mt-4"
        >
          <Form.Item
            name="name"
            label="Tên chuyến"
            rules={[{ required: true, message: "Vui lòng nhập tên chuyến" }]}
          >
            <Input
              placeholder="Ví dụ: Khám phá Hà Nội"
              size="large"
              className="rounded-xl"
            />
          </Form.Item>

          <Form.Item
            name="address"
            label="Địa chỉ"
            rules={[{ required: true, message: "Vui lòng nhập địa chỉ" }]}
          >
            <Input
              placeholder="Ví dụ: Hà Nội, Việt Nam"
              size="large"
              className="rounded-xl"
            />
          </Form.Item>

          <Form.Item
            name="activity_group_id"
            label="Loại hoạt động"
            rules={[{ required: true, message: "Vui lòng chọn loại hoạt động" }]}
          >
            <Select
              placeholder="Chọn loại hoạt động"
              size="large"
              className="rounded-xl"
            >
              {activityGroups.map((group) => (
                <Option key={group.id} value={group.id}>
                  {group.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Mô tả"
          >
            <TextArea
              rows={4}
              placeholder="Mô tả về chuyến đi của bạn..."
              className="rounded-xl"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
